import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly msalService = inject(MsalService);
  private readonly router = inject(Router);

  public backendLogin = signal(false);
  public accessToken = signal<string | null>(null);

  login(): void {
    this.backendLogin.set(true);

    this.msalService.instance.handleRedirectPromise()
      .then(() => {
        this.setAccessToken();
      })
      .catch(() => {
        this.backendLogin.set(false);
      });
  }

  logout(): void {
    this.reset();
    this.msalService.instance.initialize().then(() => {
      this.msalService.instance.setActiveAccount(null);
      this.msalService.instance.logoutRedirect();
    });
  }

  isAlreadyLoggedIn(): boolean {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0;
  }

  setAccessToken(): void {
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length === 0) {
      this.msalService.loginRedirect();
    }
    else {
      const silentRequest = {
        account: accounts[0],
        scopes: ['openid', environment.msalConfig.defaultScopeName]
      };

      this.msalService.instance
        .acquireTokenSilent(silentRequest)
        .then(response => {
          this.msalService.instance.setActiveAccount(accounts[0]);

          this.accessToken.set(response.accessToken);
          this.redirect(accounts[0]);
        })
        .catch(error => {
          if (error instanceof InteractionRequiredAuthError) {
            this.msalService.instance.acquireTokenRedirect(silentRequest);
          } else {
            this.router.navigate(['/unauthorized']);
          }
        });
    }
  }

  redirect(accountInfo: AccountInfo): void {
    this.backendLogin.set(false);
    this.router.navigate(['/dashboard']);
  }

  reset(): void {
    this.accessToken.set(null);
  }
}
